import type { AdditionalGear, RequestedAdditionalGear } from '../models/AdditionalGear';

export function gearSelectionKey(gear: { id: string; source: string }): string {
  return `${gear.source}:${gear.id}`;
}

export function cloneGearSelection(gears: RequestedAdditionalGear[]): RequestedAdditionalGear[] {
  return gears.map((gear) => ({ id: gear.id, source: gear.source }));
}

export function sameGearSelection(
  left: RequestedAdditionalGear[],
  right: RequestedAdditionalGear[],
): boolean {
  if (left.length !== right.length) return false;
  const keys = new Set(left.map(gearSelectionKey));
  return right.every((gear) => keys.has(gearSelectionKey(gear)));
}

/** Gear checked on every session. Editing "all sessions" starts from this set. */
export function gearsOnEverySession(sessions: RequestedAdditionalGear[][]): RequestedAdditionalGear[] {
  if (sessions.length === 0) return [];
  return sessions[0].filter((gear) =>
    sessions.every((list) =>
      list.some((other) => other.id === gear.id && other.source === gear.source),
    ),
  );
}

export function sessionsShareGear(sessions: RequestedAdditionalGear[][]): boolean {
  if (sessions.length < 2) return true;
  return sessions.every((list) => sameGearSelection(list, sessions[0]));
}

/**
 * Keep each session's gear when its start time is unchanged.
 * A newly added session copies the first session.
 */
export function alignSessionGears(
  previousStarts: Date[],
  nextStarts: Date[],
  previousGears: RequestedAdditionalGear[][],
  fallback: RequestedAdditionalGear[],
): RequestedAdditionalGear[][] {
  if (nextStarts.length === 0) return [];
  const shared = cloneGearSelection(previousGears[0] ?? fallback);
  if (nextStarts.length === 1 && previousStarts.length <= 1) {
    return [cloneGearSelection(previousGears[0] ?? fallback)];
  }
  const byTime = new Map<number, RequestedAdditionalGear[]>();
  previousStarts.forEach((start, index) => {
    byTime.set(start.getTime(), previousGears[index] ?? shared);
  });
  return nextStarts.map((start) => cloneGearSelection(byTime.get(start.getTime()) ?? shared));
}

export function gearAllowedOnSession(
  gears: RequestedAdditionalGear[],
  catalog: AdditionalGear[] | null | undefined,
): RequestedAdditionalGear[] {
  if (!catalog) return gears;
  const allowed = new Set(catalog.map(gearSelectionKey));
  return gears.filter((gear) => allowed.has(gearSelectionKey(gear)));
}
